import React, { useState, useEffect } from 'react';
import styled from 'theme/styled';
import useImageLoader from 'core/hooks/image-loader';
import { Area, Point } from 'react-easy-crop/types';
import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
  Button,
} from '@mui/material';
import Cropper from 'react-easy-crop';
import p from 'translation';
import { getCroppedImage } from 'core/crop-image/helpers';

interface Props {
  open: boolean;
  unprocessedImage?: File;
  onSelect: (image: Blob) => Promise<void>;
  onCancel: () => void;
}

export default function CropProfilePictureDialog({
  open,
  unprocessedImage,
  onSelect,
  onCancel,
}: Props): JSX.Element {
  const image = useImageLoader(unprocessedImage);
  const [processing, setProcessing] = useState<boolean>(false);
  const [pixelArea, setPixelArea] = useState<Area | undefined>(undefined);
  const [cropSizeValid, setCropSizeValid] = useState<boolean>(true);
  const [rotation, setRotation] = useState<number>(0);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);

  useEffect(() => {
    if (!pixelArea) return;
    setCropSizeValid(Math.min(pixelArea.height, pixelArea.width) > 500);
  }, [pixelArea]);

  return (
    <Dialog open={open}>
      <DialogContent dividers>
        <CropperContainer>
          <Cropper
            classes={{ containerClassName: 'crop-container' }}
            image={image}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={deg => {
              // The react-easy-crop allows for multi-touch rotation, but we do not want to
              // support that, so only allowing rotations of 45, 90, 180, 0.
              if (deg % 45 === 0) {
                setRotation(deg);
              }
            }}
            onCropComplete={(_, pixelCropArea) => setPixelArea(pixelCropArea)}
            cropShape='round'
          />
          {!cropSizeValid && image && (
            <CropSizeInvalidContainer>
              <Typography variant='caption' color='error'>
                {p.t('core.crop-image.zoom-out')}
              </Typography>
            </CropSizeInvalidContainer>
          )}
        </CropperContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{p.t('common.cancel')}</Button>
        <Button
          color='primary'
          variant='contained'
          disabled={processing}
          onClick={async () => {
            if (!pixelArea || !image) return;
            setProcessing(true);
            const file = await getCroppedImage(image, pixelArea, rotation);
            await onSelect(file);
            setProcessing(false);
          }}
        >
          {p.t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const CropperContainer = styled.div`
  /* position: relative; */
  width: 100%;
  height: 100%;
  min-width: 500px;
  min-height: 500px;

  .crop-container {
    margin-bottom: ${({ theme }) => theme.spacing(7)};
  }
`;

const CropSizeInvalidContainer = styled.div`
  background: ${({ theme }) => theme.palette.background.paper};
  z-index: 1000;
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0);
  bottom: 4rem;
  padding: 0.5rem;
  border-radius: 4px;
`;
